import React, { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Plane } from "lucide-react";

const blank = { code: "", name: "", logo_url: "", active: true, file: null };

const fullUrl = (u) =>
  !u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;

export default function AdminAirlineLogos() {
  const [rows, setRows] = useState([]);
  const [editor, setEditor] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/airline-logos");
      setRows(data || []);
    } catch { toast.error("Failed to load airline logos"); }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const openCreate = () => setEditor({ mode: "create", row: { ...blank } });
  const openEdit   = (r) => setEditor({ mode: "edit",   row: { ...r, file: null } });

  // ── Create / edit save handler — single multipart POST for create (carries the
  // file), JSON PATCH for edit (PATCH + separate /upload if a new file was picked).
  const save = async () => {
    const r = editor.row;
    if (editor.mode === "create" && r.code?.length !== 2) {
      toast.error("IATA code must be exactly 2 letters"); return;
    }
    if (!r.name?.trim()) { toast.error("Airline name required"); return; }
    setBusy(true);
    try {
      if (editor.mode === "create") {
        const form = new FormData();
        form.append("code",   r.code.toUpperCase());
        form.append("name",   r.name);
        form.append("active", r.active ? "true" : "false");
        if (r.file) form.append("file", r.file);
        await api.post("/admin/airline-logos/create-with-logo", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Airline added");
      } else {
        await api.patch(`/admin/airline-logos/${r.code}`, {
          name: r.name, active: !!r.active,
        });
        if (r.file) {
          const form = new FormData();
          form.append("file", r.file);
          await api.post(`/admin/airline-logos/${r.code}/upload`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
        toast.success("Updated");
      }
      setEditor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete ${code}?`)) return;
    await api.delete(`/admin/airline-logos/${code}`);
    toast.success("Deleted");
    load();
  };

  const toggleActive = async (r) => {
    await api.patch(`/admin/airline-logos/${r.code}`, { active: !r.active });
    load();
  };

  // Local preview for the picked file (data URL) so the admin sees what they
  // selected before saving. No size limit — backend auto-resizes any input.
  const pickFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    const reader = new FileReader();
    reader.onload = () => setEditor((s) => ({ ...s, row: { ...s.row, file, logo_url: reader.result } }));
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4" data-testid="admin-airline-logos">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Airline Logos</h1>
          <p className="text-slate-500 mt-1">Manage carrier logos shown across the B2C site. Upload any image — we auto-resize to 200×200 PNG.</p>
        </div>
        <Button onClick={openCreate} data-testid="airline-create-btn" className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">
          <Plus className="w-4 h-4 mr-1" /> Add airline
        </Button>
      </div>

      <Card className="overflow-x-auto">
        <Table className="min-w-[800px]">
          <TableHeader>
            <TableRow>
              <TableHead>Logo</TableHead>
              <TableHead>IATA</TableHead>
              <TableHead>Airline</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead>Active</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.code} data-testid={`airline-row-${r.code}`}>
                <TableCell>
                  <div className="w-12 h-12 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden">
                    {r.logo_url
                      ? <img src={fullUrl(r.logo_url)} alt={r.code} className="max-w-full max-h-full object-contain p-1.5" />
                      : <Plane className="w-5 h-5 text-slate-300" />}
                  </div>
                </TableCell>
                <TableCell className="font-mono font-bold text-[var(--brand-accent)]">{r.code}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.updated_at ? new Date(r.updated_at).toLocaleDateString("en-GB") : "—"}<br/>by {r.updated_by || "—"}</TableCell>
                <TableCell>
                  <Switch checked={!!r.active} onCheckedChange={() => toggleActive(r)} data-testid={`airline-active-${r.code}`} />
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`airline-edit-${r.code}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.code)} data-testid={`airline-delete-${r.code}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No airline logos yet — click &quot;Add airline&quot;.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent data-testid="airline-dialog">
          <DialogHeader>
            <DialogTitle>{editor?.mode === "edit" ? `Edit ${editor.row.code}` : "Add airline"}</DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="space-y-4">
              {/* Logo picker */}
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-xl bg-slate-50 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {editor.row.logo_url || editor.row.file
                    ? <img src={editor.row.logo_url?.startsWith("data:") ? editor.row.logo_url : fullUrl(editor.row.logo_url)} alt="" className="max-w-full max-h-full object-contain p-2" />
                    : <Plane className="w-8 h-8 text-slate-300" />}
                </div>
                <div className="flex-1">
                  <Label className="text-sm">Logo (PNG, JPG, SVG, WebP, GIF, HEIC)</Label>
                  <p className="text-xs text-slate-500 mb-2">Upload any size — auto-resized to 200×200 PNG with transparency preserved.</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="airline-file-input"
                    onChange={(e) => pickFile(e.target.files?.[0])}
                  />
                  <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-1" /> {editor.row.file ? "Replace file" : (editor.row.logo_url ? "Replace logo" : "Choose file")}
                  </Button>
                  {editor.row.file && (
                    <span className="ml-2 text-xs text-slate-500">{editor.row.file.name} · {(editor.row.file.size/1024).toFixed(1)} KB</span>
                  )}
                </div>
              </div>

              <div>
                <Label>IATA code (2 letters)</Label>
                <Input
                  data-testid="airline-code"
                  className="h-10 font-mono uppercase"
                  value={editor.row.code}
                  disabled={editor.mode === "edit"}
                  maxLength={2}
                  placeholder="e.g. SV, EK, BA"
                  onChange={(e) => setEditor({ ...editor, row: { ...editor.row, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") } })}
                />
              </div>
              <div>
                <Label>Airline name</Label>
                <Input
                  data-testid="airline-name"
                  className="h-10"
                  placeholder="e.g. Saudi Arabian Airlines"
                  value={editor.row.name}
                  onChange={(e) => setEditor({ ...editor, row: { ...editor.row, name: e.target.value } })}
                />
              </div>
              <div className="flex items-center gap-3 pt-1">
                <Switch
                  data-testid="airline-active-switch"
                  checked={!!editor.row.active}
                  onCheckedChange={(v) => setEditor({ ...editor, row: { ...editor.row, active: !!v } })}
                />
                <Label className="cursor-pointer">Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="airline-save" className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">
              {busy ? "Saving…" : (editor?.mode === "edit" ? "Save changes" : "Add airline")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
